<template>
  <figure>
    <Image :filename='filename' :context='kql.site' sizes='100vw' @image-loaded="imageLoaded" />
    <Responsive v-slot="{ breakpoint }">
      <ParticlesGrid :radius='breakpoint == "narrow" ? 0.5 : 1' v-if="showParticles" />
    </Responsive>
  </figure>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { yamlFirst } from '@/utils'; // Import utility function
import { ref, watch, onMounted } from 'vue';
import { tracker } from '@/directives/v-scene';
import { useWindowSize } from '@vueuse/core';

const kql = useStore();
const props = defineProps(['selection', 'sources']);
const filename = computed(() => yamlFirst(props.selection).trim());
const isImageLoaded = ref(false);
const showParticles = ref(false);
const isPageTallerThanViewport = ref(false);

const trackedElements = tracker.topTwoScenes;
const { height: windowHeight } = useWindowSize();

// Check if document is taller than viewport
const checkDocumentHeight = () => {
  const docHeight = document.documentElement.scrollHeight;
  isPageTallerThanViewport.value = docHeight > windowHeight.value;
};

// Handle image loaded event
const imageLoaded = () => {
  isImageLoaded.value = true;
  checkReadyToShow();
};

// Check if we're ready to show particles
const checkReadyToShow = () => {
  checkDocumentHeight();

  if (isImageLoaded.value && trackedElements.value.primary && isPageTallerThanViewport.value) {
    // Small delay to ensure everything is properly initialized
    setTimeout(() => {
      showParticles.value = true;
    }, 100);
  }
};

// Watch for tracked elements to be ready
watch(() => trackedElements.value.primary, (primary) => {
  if (primary) {
    checkReadyToShow();
  }
});

// Watch for window resize
watch(windowHeight, () => {
  if (!showParticles.value) {
    checkReadyToShow();
  }
});

// Recheck on mount and after a delay (for content loading)
onMounted(() => {
  setTimeout(checkReadyToShow, 100);
  setTimeout(checkReadyToShow, 500);
});
</script>

<style scoped>
figure {
  width: 100%;
  height: 90vh;
}

@media (max-width: 1080px) {
  figure {
    height: 60vh;
  }
}

img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 90%, transparent 99%);
}
</style>
